import { createElement, Fragment, type ReactNode } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { de } from './de';
import { en } from './en';

/**
 * Slovak is the source language: texts are written in Slovak and wrapped in t(); the English and
 * German dictionaries are keyed by the Slovak text. A text missing from a dictionary stays Slovak.
 * Check the dictionaries with `npm run i18n:check`.
 */
export type Language = 'sk' | 'en' | 'de';

export const LANGUAGES: { code: Language; label: string }[] = [
  { code: 'sk', label: 'Slovenčina' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

const DICTIONARIES: Record<Exclude<Language, 'sk'>, Record<string, string>> = { en, de };
const LOCALES: Record<Language, string> = { sk: 'sk-SK', en: 'en-GB', de: 'de-DE' };

interface LanguageState {
  language: Language;
  setLanguage: (language: Language) => void;
}

export const useLanguageStore = create<LanguageState>()(
  persist((set) => ({ language: 'sk', setLanguage: (language) => set({ language }) }), { name: 'car-evidence-language' }),
);

export const getLanguage = (): Language => useLanguageStore.getState().language;

/** BCP 47 locale for dates and numbers in the current language. */
export const locale = () => LOCALES[getLanguage()];

export const isLanguage = (value: unknown): value is Language => LANGUAGES.some((l) => l.code === value);

/** Translate a Slovak text; {name} placeholders are filled from `params`. */
export function t(text: string, params?: Record<string, string | number | null | undefined>): string {
  const language = getLanguage();
  const translated = language === 'sk' ? text : (DICTIONARIES[language][text] ?? text);
  if (!params) return translated;
  return translated.replace(/\{(\w+)\}/g, (match, key: string) => (key in params ? String(params[key] ?? '') : match));
}

/**
 * Slovak label map that translates on every read, for maps defined once at module level
 * (CAR_STATUS_LABELS[status], Object.entries(...) and toOptions(...) all see the current language).
 */
export function tr<T extends Record<string, string>>(labels: T): T {
  return new Proxy(labels, {
    get: (target, key) => {
      const value = Reflect.get(target, key);
      return typeof value === 'string' ? t(value) : value;
    },
  });
}

/** Like t(), but placeholders may be elements: tx('Účet {name} má…', { name: <strong>{n}</strong> }). */
export function tx(text: string, params: Record<string, ReactNode>): ReactNode {
  return t(text)
    .split(/(\{\w+\})/)
    .map((part, i) => {
      const key = /^\{(\w+)\}$/.exec(part)?.[1];
      return createElement(Fragment, { key: i }, key !== undefined && key in params ? params[key] : part);
    });
}
